import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { GasType, GasTypeUpdate } from '../../types/gasTypes';
import GasTypeInput from './GasTypeInput';
import Button from './common/Button';

interface GasTypesCarouselProps {
  gasTypes: GasType[];
  onUpdate: (id: number, field: keyof GasTypeUpdate, value: string | number) => void;
  onRemove: (id: number) => void;
  onAdd: () => void;
}

const GasTypesCarousel: React.FC<GasTypesCarouselProps> = ({
  gasTypes,
  onUpdate,
  onRemove,
  onAdd
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, index: 0 });
  const [dragOffset, setDragOffset] = useState(0);
  const [scrollShadows, setScrollShadows] = useState({ left: false, right: true });

  // Calculate how many items to show based on screen size
  const getItemsPerView = () => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1920) return gasTypes.length; // Show all items on ultra-wide screens
      if (width >= 1536) return gasTypes.length; // Show all items on XL screens
      if (width >= 1024) return gasTypes.length; // Show all items on desktop
      if (width >= 768) return 2;  // Tablet
      return 1; // Mobile
    }
    return gasTypes.length; // Default for server-side rendering
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  useEffect(() => {
    const handleResize = () => {
      const newItemsPerView = getItemsPerView();
      const newIsMobile = window.innerWidth < 1024;
      setItemsPerView(newItemsPerView);
      setIsMobile(newIsMobile);
      
      // Reset current index if switching to desktop mode
      if (!newIsMobile && currentIndex > 0) {
        setCurrentIndex(0);
      }
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex, gasTypes.length]);

  // Handle carousel position when gas types are removed
  useEffect(() => {
    const maxIndex = Math.max(0, gasTypes.length - itemsPerView);
    if (currentIndex > maxIndex) {
      // If current index is beyond the new max, move to the previous available slide
      setCurrentIndex(Math.max(0, maxIndex));
    }
  }, [gasTypes.length, itemsPerView, currentIndex]);

  // Handle scroll shadow visibility on desktop
  useEffect(() => {
    if (isMobile || !viewportRef.current) return;

    const viewport = viewportRef.current;
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = viewport;
      const maxScroll = scrollWidth - clientWidth;
      
      setScrollShadows({
        left: scrollLeft > 5, // Show left shadow when scrolled right
        right: scrollLeft < maxScroll - 5, // Show right shadow when not at end
      });
    };

    // Initial check
    handleScroll();
    
    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [isMobile, gasTypes.length]);

  const totalItems = gasTypes.length;
  const maxIndex = Math.max(0, totalItems - itemsPerView);

  const nextSlide = () => {
    if (isMobile) {
      setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
    }
  };

  const prevSlide = () => {
    if (isMobile) {
      setCurrentIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const goToSlide = (index: number) => {
    if (isMobile) {
      setCurrentIndex(Math.min(index, maxIndex));
    }
  };

  // Touch/drag handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX,
      index: currentIndex
    });
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging) return;

    const touch = e.touches[0];
    const offset = touch.clientX - dragStart.x;
    setDragOffset(offset);
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging) return;

    setIsDragging(false);
    const threshold = 50; // Minimum distance to trigger slide change
    
    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0 && currentIndex > 0) {
        // Swiped right - go to previous slide
        setCurrentIndex(prev => Math.max(prev - 1, 0));
      } else if (dragOffset < 0 && currentIndex < maxIndex) {
        // Swiped left - go to next slide
        setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
      }
    }
    
    setDragOffset(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isMobile) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      index: currentIndex
    });
    setDragOffset(0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMobile || !isDragging) return;

    const offset = e.clientX - dragStart.x;
    setDragOffset(offset);
  };

  const handleMouseUp = () => {
    if (!isMobile || !isDragging) return;

    setIsDragging(false);
    const threshold = 50;
    
    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0 && currentIndex > 0) {
        setCurrentIndex(prev => Math.max(prev - 1, 0));
      } else if (dragOffset < 0 && currentIndex < maxIndex) {
        setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
      }
    }
    
    setDragOffset(0);
  };

  return (
    <div className="card-professional">
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          <span className="hidden sm:inline">Available Gas Types</span>
          <span className="sm:hidden">Gas Types</span>
        </h2>
        <div className="flex items-center gap-4">
          {/* Navigation controls - only show on mobile when needed */}
          {isMobile && totalItems > itemsPerView && (
            <div className="carousel-container">
              <button
                onClick={prevSlide}
                disabled={currentIndex === 0}
                className="carousel-nav-button"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              {/* Dots indicator */}
              <div className="carousel-dots">
                {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextSlide}
                disabled={currentIndex >= maxIndex}
                className="carousel-nav-button"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {/* Add button */}
          <Button
            variant="primary"
            onClick={onAdd}
            icon={Plus}
            className="px-3 sm:px-4 py-2 [&>div]:gap-0 sm:[&>div]:gap-2"
          >
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Gas Type</span>
          </Button>
        </div>
      </div>

      {/* Carousel container */}
      <div 
        ref={wrapperRef}
        className={`carousel-container-wrapper ${!isMobile ? 'desktop-carousel' : ''}`}
        style={!isMobile ? {
          '--shadow-left-opacity': scrollShadows.left ? '1' : '0',
          '--shadow-right-opacity': scrollShadows.right ? '1' : '0'
        } as React.CSSProperties : undefined}
      >
        <div 
          ref={viewportRef}
          className={`carousel-viewport ${isMobile ? 'mobile-carousel' : ''}`}
        >
          <div 
            ref={trackRef}
            className={`carousel-track ${isMobile ? 'touch-enabled' : ''}`}
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)${
              isDragging && isMobile ? ` translateX(${dragOffset}px)` : ''
            }`,
            transition: isDragging ? 'none' : 'transform 0.3s ease-in-out'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {gasTypes.map((gas) => (
            <div 
              key={gas.id}
              className="carousel-slide"
              style={{ 
                width: isMobile ? `${100 / itemsPerView}%` : `${100 / gasTypes.length}%`,
                minWidth: isMobile ? 'auto' : '280px'
              }}
            >
              <GasTypeInput
                gasType={gas}
                canRemove={gasTypes.length > 2}
                onUpdate={onUpdate}
                onRemove={onRemove}
              />
            </div>
          ))}
          </div>
        </div>
      </div>

      {/* Carousel info */}
      <div className="mt-4 text-center">
        {isMobile ? (
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Showing {Math.min(currentIndex + itemsPerView, totalItems)} of {totalItems} gas types
            {totalItems > itemsPerView && (
              <span className="block mt-1">Swipe left/right to navigate</span>
            )}
          </p>
        ) : (
          totalItems > 2 && (
            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              {totalItems} gas types • Scroll horizontally to see all
            </p>
          )
        )}
      </div>
    </div>
  );
};

export default GasTypesCarousel;